import {
  RefObject,
  SelectHTMLAttributes,
  useState,
  useEffect,
  forwardRef,
} from "react";
import styled from "styled-components";
import { Typography } from "./Typography";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";

export interface ISelect extends SelectHTMLAttributes<HTMLSelectElement> {
  title: string;
  options: IOption[];
  ref: RefObject<HTMLSelectElement>;
  selectedProvider: string;
  onSelected: (value: string) => any;
  placeholder?: string;
}

interface IOption {
  value: string;
  label: string;
}

const Wrapper = styled.div`
  width: 100%;
  position: relative;
`;
const HiddenSelect = styled.select`
  display: none;
`;
const InputContainer = styled.div`
  height: 39px;
  color: #3f2c26;
  border: 1px solid rgba(0, 0, 0, 0.3);
  border-radius:0;
  outline: none;
  padding: 0 0.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  cursor: pointer;
  position: relative;
  background-color: #fff;
  @media (min-width: 800px) {
    margin-bottom: 0;
    max-height: 38px;
    padding: 0;
    padding-left: 1rem;
  }
`;
const WrapperOptions = styled.div`
  position: absolute;
  top: 78px;
  width: 100%;
  border-radius: 0;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.3);
  border-top: 0.5px solid rgba(0, 0, 0, 0.3);
  z-index: 10;
  max-height: 320px;
  overflow: auto;
`;
const Option = styled.div`
  position: relative;
  overflow: hidden;
  height: 39px;
  color: #3f2c26;
  outline: none;
  padding: 0 20px;
  display: flex;
  background-color: #fff;
  align-items: center;
  padding-left: 30px;
  cursor: pointer;
  &::before {
    content: "";
    width: 90%;
    height: 1px;
    background-color: rgba(0, 0, 0, 0.3);
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
  }
  &:last-child {
    &::before {
      content: "";
      display: none;
    }
  }
  &:hover {
    background-color: #eee;
  }
`;
const StyledTypography = styled(Typography)`
  margin-bottom: 0.25rem;
  @media (min-width: 800px) {
    margin-bottom: 0.5rem;
  }
`;
const StyledUpIcon = styled(FontAwesomeIcon)`
  position: absolute;
  right: 10px;
`;
const StyledDownIcon = styled(FontAwesomeIcon)`
  position: absolute;
  right: 10px;
`;

export const Select = forwardRef<HTMLSelectElement, ISelect>(
  (
    { options, selectedProvider, onSelected, title },
    ref
  ) => {
    const [value, setValue] = useState(selectedProvider);
    const [isExpanded, setExpanded] = useState(false);

    const displayValue = options.find((option) => value === option.value)?.label;

    const handleSelect = (
      event: React.MouseEvent<HTMLDivElement>,
      option: IOption
    ) => {
      event.stopPropagation();
      setValue(option.value);
      setExpanded(false);
    };

    useEffect(() => {
      if (selectedProvider) {
        setValue(selectedProvider);
      } else {
        setValue("");
      }
    }, [selectedProvider]);

    useEffect(() => {
      onSelected(value);
    }, [value]);

    return (
      <Wrapper>
        <HiddenSelect ref={ref} defaultValue={selectedProvider}>
          <option value="" />
          {options.map((option) => (
            <option key={option.label} value={option.value}>
              {option.label}
            </option>
          ))}
        </HiddenSelect>
        <div>
          <StyledTypography variant="body">{title}</StyledTypography>
          <InputContainer
            onClick={() => setExpanded((prevState) => !prevState)}
            role="tab"
            tabIndex="0"
          >
            {value && value.length === 0 ? (
              <p
                style={{
                  color: "#757575",
                  fontFamily: "Lato",
                  fontSize: "16px",
                  fontWeight: "400",
                }}
              ></p>
            ) : (
              displayValue
            )}
            {isExpanded ? (
              <StyledUpIcon icon={faChevronUp} />
            ) : (
              <StyledDownIcon icon={faAngleDown} />
            )}
          </InputContainer>
        </div>
        {isExpanded && (
          <WrapperOptions>
            {options.map((option) => (
              <Option
                key={option.label}
                onClick={(event) => handleSelect(event, option)}
              >
                {option.label}
              </Option>
            ))}
          </WrapperOptions>
        )}
      </Wrapper>
    );
  }
);
